import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';
import {
  formatExamDate, daysUntilExamDate, registrationState, sortExamsByNextTest, normalizeExternalUrl,
  examSchedule,
} from '../utils/examDates';

const NAVY      = '#0D1B2A';
const NAVY_MID  = '#132233';
const NAVY_DEEP = '#091420';
const AMBER     = '#F5A623';
const WHITE     = '#FFFFFF';
const GREEN     = '#1D9E75';
const GRAY      = '#6B7280';
const ATTENTION_RED_TEXT = '#F09595';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

const inp = {
  width: '100%', padding: '9px 12px', fontSize: '0.88rem',
  border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: '8px',
  color: WHITE, outline: 'none', backgroundColor: 'rgba(255,255,255,0.06)',
  fontFamily: FONT_BODY,
};
const sel = { ...inp, cursor: 'pointer' };
const txt = { ...inp, resize: 'vertical', minHeight: '80px' };
const lbl = { display: 'block', fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '5px' };
const fld = { marginBottom: '1rem' };
const g2  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' };
const kicker = { fontSize: '0.64rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' };
const ghostBtn = {
  background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.75)',
  fontSize: '0.78rem', fontWeight: 600, padding: '6px 12px', borderRadius: '7px', cursor: 'pointer', fontFamily: FONT_BODY,
};
const dangerBtn = {
  ...ghostBtn, background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.35)', color: '#F87171',
};

const EMPTY_EXAM_FORM = {
  name: '', description: '',
  location: '', passing_score: '', exam_fee: '', study_guide_url: '',
  scheduled_individually: false,
  contact_name: '', contact_title: '', contact_phone: '', contact_email: '',
};

// Sessions live in their own table and are edited as a list, so they sit outside the flat
// form object. Exams predating union_exam_sessions open with their legacy single date as the
// first row, so editing one migrates it instead of silently dropping it.
function examToSessions(exam) {
  const rows = (exam?.sessions || [])
    .filter(s => s?.test_date)
    .sort((a, b) => String(a.test_date).localeCompare(String(b.test_date)))
    .map(s => ({ test_date: s.test_date, registration_deadline: s.registration_deadline || '' }));
  if (rows.length) return rows;
  if (exam?.next_test_date) {
    return [{ test_date: exam.next_test_date, registration_deadline: exam.registration_deadline || '' }];
  }
  return [];
}

function examToForm(exam) {
  return Object.fromEntries(Object.keys(EMPTY_EXAM_FORM).map(k => [
    k, exam?.[k] ?? (typeof EMPTY_EXAM_FORM[k] === 'boolean' ? false : ''),
  ]));
}

function IconX() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function statusBadge(appr, t) {
  if (appr.archived_at)           return { label: t('union.dashboard.apprenticeships.archivedBadge'), color: GRAY };
  if (appr.status === 'closed')   return { label: t('union.dashboard.apprModal.closedStatus'), color: GRAY };
  if (appr.status === 'draft')    return { label: t('union.dashboard.apprModal.draftStatus'), color: AMBER };
  return null;
}

// ── Exam "bulletin" card ──────────────────────────────────────────────────────
function ExamBulletin({ exam, linkedApprs, linkableApprs, busy, onEdit, onDelete, onLink, onUnlink, hasAnyApprenticeships }) {
  const { t } = useTranslation();
  // The live session drives every date on this card, so the union sees exactly what a worker
  // sees -- not whichever date happens to sit on the legacy column.
  const schedule = examSchedule(exam);
  const sessionCount = (exam.sessions || []).filter(s => s?.test_date).length;
  const regState = schedule.individual ? null : registrationState({ registration_deadline: schedule.registration_deadline });
  const regDays  = daysUntilExamDate(schedule.registration_deadline);
  const testDays = daysUntilExamDate(schedule.test_date);
  const testPassed = !schedule.individual && testDays !== null && testDays < 0;

  const noticeTag = schedule.individual
    ? { text: t('exams.union.individuallyScheduledTag'), color: NAVY, bg: 'rgba(255,255,255,0.75)' }
    : testPassed
    ? { text: t('exams.union.testDatePassed'), color: ATTENTION_RED_TEXT, bg: 'rgba(226,75,74,0.15)' }
    : regState === 'closed'
      ? { text: t('exams.shared.registrationClosed'), color: ATTENTION_RED_TEXT, bg: 'rgba(226,75,74,0.15)' }
      : regState === 'closingSoon'
        ? { text: regDays === 0 ? t('exams.shared.closesToday') : t('exams.shared.closesInDays', { count: regDays }), color: NAVY, bg: AMBER }
        : regState === 'open'
          ? { text: t('exams.union.registrationOpen'), color: GREEN, bg: 'rgba(29,158,117,0.15)' }
          : null;

  const cells = [
    schedule.individual
      ? { key: 'test', label: t('exams.worker.scheduleLabel'), value: t('exams.union.individuallyScheduledTag') }
      : {
          key: 'test', label: t('exams.shared.nextTestDateLabel'),
          value: formatExamDate(schedule.test_date, { weekday: true }) || t('exams.shared.notSet'),
          // With several dates on the books, say which one this is out of how many.
          sub: [
            testDays === null ? null : testDays === 0 ? t('exams.union.today') : testDays > 0 ? t('exams.union.inDays', { count: testDays }) : null,
            sessionCount > 1 ? t('exams.union.sessionsCount', { count: sessionCount }) : null,
          ].filter(Boolean).join(' · ') || null,
          empty: !schedule.test_date,
        },
    !schedule.individual && { key: 'deadline', label: t('exams.shared.registrationDeadlineLabel'), value: formatExamDate(schedule.registration_deadline) || t('exams.shared.notSet'), empty: !schedule.registration_deadline },
    { key: 'location', label: t('exams.shared.locationLabel'),     value: exam.location || t('exams.shared.notSet'),      empty: !exam.location },
    { key: 'score',    label: t('exams.shared.passingScoreLabel'), value: exam.passing_score || t('exams.shared.notSet'), empty: !exam.passing_score },
    { key: 'fee',      label: t('exams.shared.examFeeLabel'),      value: exam.exam_fee || t('exams.shared.notSet'),      empty: !exam.exam_fee },
  ].filter(Boolean);

  return (
    <article data-exam-id={exam.id} style={{ backgroundColor: NAVY_MID, border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', overflow: 'hidden' }}>
      {/* Notice strip */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap',
        padding: '8px 16px', backgroundColor: 'rgba(245,166,35,0.07)', borderBottom: '1px dashed rgba(245,166,35,0.35)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span style={{ ...kicker, color: AMBER }}>{t('exams.union.examNotice')}</span>
          {noticeTag && (
            <span style={{ backgroundColor: noticeTag.bg, color: noticeTag.color, borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '2px 9px' }}>
              {noticeTag.text}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={onEdit} style={ghostBtn}>{t('union.dashboard.shared.editButton')}</button>
          <button onClick={onDelete} style={dangerBtn}>{t('exams.union.deleteButton')}</button>
        </div>
      </div>

      <div style={{ padding: '14px 16px 16px' }}>
        <h3 style={{ fontFamily: FONT_HEAD, fontSize: '1.65rem', letterSpacing: '0.03em', color: WHITE, lineHeight: 1.05, overflowWrap: 'anywhere' }}>
          {exam.name}
        </h3>
        {exam.description && (
          <p style={{ fontSize: '0.84rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.55, marginTop: '4px', whiteSpace: 'pre-line' }}>
            {exam.description}
          </p>
        )}

        {/* Structured fact grid -- hairline-ruled cells, like a posted notice. Each cell draws its
            own top/left rule, pulled 1px outward so the container clips the outer edge; an unfilled
            last row then stays plain navy instead of exposing a lighter gap. */}
        <dl style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          backgroundColor: NAVY, border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '8px', overflow: 'hidden', margin: '14px 0 0',
        }}>
          {cells.map(c => (
            <div key={c.key} style={{ padding: '9px 12px', minWidth: 0, marginTop: '-1px', marginLeft: '-1px', borderTop: '1px solid rgba(255,255,255,0.08)', borderLeft: '1px solid rgba(255,255,255,0.08)' }}>
              <dt style={{ ...kicker, fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)', marginBottom: '3px' }}>{c.label}</dt>
              <dd style={{ margin: 0, fontSize: '0.86rem', fontWeight: 600, color: c.empty ? 'rgba(255,255,255,0.3)' : WHITE, overflowWrap: 'anywhere' }}>
                {c.value}
              </dd>
              {c.sub && <dd style={{ margin: '2px 0 0', fontSize: '0.72rem', color: AMBER, fontWeight: 600 }}>{c.sub}</dd>}
            </div>
          ))}
        </dl>

        {exam.study_guide_url && (
          <a href={exam.study_guide_url} target="_blank" rel="noopener noreferrer"
            style={{ display: 'inline-block', marginTop: '10px', fontSize: '0.8rem', fontWeight: 600, color: AMBER, textDecoration: 'none', overflowWrap: 'anywhere' }}>
            {t('exams.union.studyGuideLink')} ↗
          </a>
        )}

        {/* Linked postings */}
        <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <p style={{ ...kicker, color: 'rgba(255,255,255,0.45)', marginBottom: '8px' }}>
            {t('exams.union.requiredFor')}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
            {linkedApprs.length === 0 && (
              <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)', marginRight: '4px' }}>
                {t('exams.union.notLinkedYet')}
              </span>
            )}
            {linkedApprs.map(appr => {
              const badge = statusBadge(appr, t);
              return (
                <span key={appr.id} data-linked-appr={appr.id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px', maxWidth: '100%',
                  backgroundColor: 'rgba(245,166,35,0.12)', border: '1px solid rgba(245,166,35,0.35)',
                  borderRadius: '6px', padding: '4px 4px 4px 10px', fontSize: '0.8rem', color: WHITE, fontWeight: 600,
                  opacity: badge ? 0.7 : 1,
                }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{appr.title}</span>
                  {badge && <span style={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.6)', fontWeight: 700 }}>· {badge.label}</span>}
                  <button
                    onClick={() => onUnlink(appr)}
                    disabled={busy}
                    aria-label={t('exams.union.unlinkAria', { title: appr.title })}
                    style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', flexShrink: 0, background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '4px', color: 'rgba(255,255,255,0.75)', cursor: 'pointer' }}
                  >
                    <IconX />
                  </button>
                </span>
              );
            })}
            {linkableApprs.length > 0 ? (
              <select
                value=""
                disabled={busy}
                onChange={e => { const appr = linkableApprs.find(a => a.id === e.target.value); if (appr) onLink(appr); }}
                aria-label={t('exams.union.linkPlaceholder')}
                style={{ ...sel, width: 'auto', maxWidth: '100%', padding: '6px 10px', fontSize: '0.8rem', border: `1.5px dashed ${AMBER}`, color: AMBER, fontWeight: 600, backgroundColor: 'transparent' }}
              >
                <option value="">{t('exams.union.linkPlaceholder')}</option>
                {linkableApprs.map(a => {
                  const badge = statusBadge(a, t);
                  return <option key={a.id} value={a.id}>{a.title}{badge ? ` (${badge.label})` : ''}</option>;
                })}
              </select>
            ) : !hasAnyApprenticeships ? (
              <span style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)' }}>{t('exams.union.postApprenticeshipFirst')}</span>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
}

// ── Plain-text requirements editor for one posting ─────────────────────────────
function RequirementsCard({ appr, linkedExams, onSave, onGoToExams }) {
  const { t } = useTranslation();
  const [draft, setDraft]   = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState(null); // { text, isError }
  const requirements = appr.requirements || [];
  const badge = statusBadge(appr, t);

  const commit = async (next) => {
    setSaving(true);
    setMsg(null);
    const ok = await onSave(appr, next);
    setSaving(false);
    setMsg(ok ? { text: t('exams.union.requirementsSaved'), isError: false } : { text: t('exams.union.saveFailed'), isError: true });
    if (ok) setTimeout(() => setMsg(null), 2500);
    return ok;
  };

  const handleAdd = async () => {
    const value = draft.trim();
    if (!value || saving) return;
    if (requirements.some(r => r.trim().toLowerCase() === value.toLowerCase())) {
      setMsg({ text: t('exams.union.requirementDuplicate'), isError: true });
      return;
    }
    if (await commit([...requirements, value])) setDraft('');
  };

  return (
    <div data-req-appr={appr.id} style={{ backgroundColor: NAVY_MID, border: '1px solid rgba(255,255,255,0.07)', borderLeft: `3px solid ${AMBER}`, borderRadius: '0 10px 10px 0', padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
        <h4 style={{ fontSize: '1rem', fontWeight: 700, color: WHITE }}>{appr.title}</h4>
        {appr.trade && (
          <span style={{ backgroundColor: 'rgba(245,166,35,0.15)', color: AMBER, borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '3px 10px' }}>{appr.trade}</span>
        )}
        {badge && (
          <span style={{ backgroundColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)', borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '3px 10px' }}>{badge.label}</span>
        )}
      </div>
      <p style={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.45)', marginBottom: '12px' }}>
        {linkedExams.length > 0
          ? <>{t('exams.union.examsLinkedPrefix')} <span style={{ color: 'rgba(255,255,255,0.75)', fontWeight: 600 }}>{linkedExams.map(e => e.name).join(', ')}</span></>
          : t('exams.union.noExamsLinked')}
        {' · '}
        <button onClick={onGoToExams} style={{ background: 'none', border: 'none', padding: 0, color: AMBER, fontSize: '0.76rem', fontWeight: 600, cursor: 'pointer', fontFamily: FONT_BODY }}>
          {t('exams.union.manageExamsLink')}
        </button>
      </p>

      {requirements.length === 0 ? (
        <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.35)', marginBottom: '10px' }}>{t('exams.union.noRequirementsYet')}</p>
      ) : (
        <ol style={{ listStyle: 'none', margin: '0 0 10px', padding: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {requirements.map((r, i) => (
            <li key={`${i}-${r}`} data-requirement={r} style={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: NAVY, borderRadius: '7px', padding: '6px 6px 6px 12px' }}>
              <span style={{ fontFamily: FONT_HEAD, fontSize: '1rem', color: AMBER, minWidth: '14px', paddingTop: '2px' }}>{i + 1}</span>
              <span style={{ flex: 1, minWidth: 0, fontSize: '0.86rem', color: 'rgba(255,255,255,0.85)', overflowWrap: 'anywhere' }}>{r}</span>
              <button
                onClick={() => commit(requirements.filter((_, idx) => idx !== i))}
                disabled={saving}
                aria-label={t('exams.union.removeRequirementAria', { requirement: r })}
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', flexShrink: 0, background: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.3)', borderRadius: '6px', color: '#F87171', cursor: 'pointer' }}
              >
                <IconX />
              </button>
            </li>
          ))}
        </ol>
      )}

      <form onSubmit={e => { e.preventDefault(); handleAdd(); }} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <input
          style={{ ...inp, flex: '1 1 200px' }}
          value={draft}
          maxLength={200}
          onChange={e => { setDraft(e.target.value); if (msg?.isError) setMsg(null); }}
          placeholder={t('exams.union.requirementPlaceholder')}
          aria-label={t('exams.union.requirementPlaceholder')}
        />
        <button
          type="submit"
          disabled={saving || !draft.trim()}
          style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.84rem', padding: '9px 16px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY, opacity: saving || !draft.trim() ? 0.55 : 1 }}
        >
          {t('exams.union.addRequirementButton')}
        </button>
      </form>
      {msg && <p style={{ fontSize: '0.78rem', fontWeight: 600, marginTop: '6px', color: msg.isError ? '#F87171' : GREEN }}>{msg.text}</p>}
    </div>
  );
}

// ── Panel ──────────────────────────────────────────────────────────────────────
export default function UnionExamsPanel({ userId, unionAccount, apprenticeships, onApprenticeshipUpdated, onPostApprenticeship }) {
  const { t } = useTranslation();
  const [view, setView]       = useState('exams'); // 'exams' | 'requirements'
  const [exams, setExams]     = useState([]);
  const [links, setLinks]     = useState([]); // [{ apprenticeship_id, exam_id }]
  const [loaded, setLoaded]   = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [busyExamId, setBusyExamId] = useState(null);
  const [actionError, setActionError] = useState('');
  const [showArchivedReqs, setShowArchivedReqs] = useState(false);

  const [modalExam, setModalExam] = useState(null); // null = closed, {} = create, exam = edit
  const [form, setForm]           = useState(EMPTY_EXAM_FORM);
  const [sessions, setSessions]   = useState([]); // [{ test_date, registration_deadline }]
  const [formError, setFormError] = useState('');
  const [savingExam, setSavingExam] = useState(false);

  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setLoaded(false);
    setLoadError(false);
    // Every failure ends the loading state and logs the exact error. A request that never
    // answers would otherwise leave "Loading…" up forever, so it gives up after 15s (an answer
    // that arrives later still replaces the error).
    const fail = (err) => {
      if (cancelled) return;
      console.error('[UnionExamsPanel] exams load failed:', err);
      setLoadError(true);
      setLoaded(true);
    };
    const timer = setTimeout(() => fail(new Error('union_exams load timed out after 15s')), 15000);
    (async () => {
      const { data: examRows, error } = await supabase
        .from('union_exams')
        .select('*')
        .eq('union_id', userId);
      if (cancelled) return;
      if (error) { clearTimeout(timer); fail(error); return; }
      const ids = (examRows || []).map(e => e.id);
      let linkRows = [];
      let sessionRows = [];
      if (ids.length) {
        const [linkRes, sessionRes] = await Promise.all([
          supabase.from('apprenticeship_exam_requirements').select('apprenticeship_id, exam_id').in('exam_id', ids),
          supabase.from('union_exam_sessions').select('id, exam_id, test_date, registration_deadline').in('exam_id', ids),
        ]);
        if (cancelled) return;
        if (linkRes.error || sessionRes.error) { clearTimeout(timer); fail(linkRes.error || sessionRes.error); return; }
        linkRows = linkRes.data || [];
        sessionRows = sessionRes.data || [];
      }
      clearTimeout(timer);
      const byExam = {};
      sessionRows.forEach(s => { (byExam[s.exam_id] = byExam[s.exam_id] || []).push(s); });
      setExams((examRows || []).map(e => ({ ...e, sessions: byExam[e.id] || [] })));
      setLinks(linkRows);
      setLoadError(false);
      setLoaded(true);
    })().catch(err => { clearTimeout(timer); fail(err); });
    return () => { cancelled = true; clearTimeout(timer); };
  }, [userId, reloadKey]);

  const sortedExams = useMemo(() => sortExamsByNextTest(exams), [exams]);
  const apprById = useMemo(() => Object.fromEntries(apprenticeships.map(a => [a.id, a])), [apprenticeships]);
  const examById = useMemo(() => Object.fromEntries(exams.map(e => [e.id, e])), [exams]);
  // Postings a new link can target: anything not archived, open ones first.
  const linkTargets = useMemo(
    () => apprenticeships.filter(a => !a.archived_at).sort((a, b) => (a.status === 'closed') - (b.status === 'closed')),
    [apprenticeships],
  );
  const postingsWithExams = new Set(links.filter(l => apprById[l.apprenticeship_id]).map(l => l.apprenticeship_id)).size;

  // ── Exam CRUD ──
  const openCreate = () => { setForm(EMPTY_EXAM_FORM); setSessions([]); setFormError(''); setModalExam({}); };
  const openEdit   = (exam) => { setForm(examToForm(exam)); setSessions(examToSessions(exam)); setFormError(''); setModalExam(exam); };
  const closeModal = () => { if (!savingExam) setModalExam(null); };

  const addSession    = () => setSessions(prev => [...prev, { test_date: '', registration_deadline: '' }]);
  const updateSession = (i, field, value) => setSessions(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s));
  const removeSession = (i) => setSessions(prev => prev.filter((_, idx) => idx !== i));

  const handleSaveExam = async () => {
    const name = form.name.trim();
    if (!name) { setFormError(t('exams.union.nameRequired')); return; }

    // Individually-scheduled exams have no dates at all, so their rows are dropped rather than
    // kept around invisibly to reappear if the toggle is ever switched back off.
    const rows = form.scheduled_individually
      ? []
      : sessions.filter(s => s.test_date).map(s => ({
          test_date: s.test_date,
          registration_deadline: s.registration_deadline || null,
        }));
    const badDeadline = rows.find(s => s.registration_deadline && s.registration_deadline > s.test_date);
    if (badDeadline) { setFormError(t('exams.union.sessionDeadlineAfterTest')); return; }
    if (new Set(rows.map(s => s.test_date)).size !== rows.length) {
      setFormError(t('exams.union.duplicateSessionDate'));
      return;
    }

    // union_exams still carries a single date, and older code paths still read it, so it is
    // kept in step with whichever session is currently live rather than left to drift.
    const live = examSchedule({ ...form, sessions: rows });
    const payload = {
      name,
      description:            form.description.trim() || null,
      next_test_date:         live.test_date || null,
      registration_deadline:  live.registration_deadline || null,
      location:               form.location.trim() || null,
      passing_score:          form.passing_score.trim() || null,
      exam_fee:               form.exam_fee.trim() || null,
      study_guide_url:        normalizeExternalUrl(form.study_guide_url) || null,
      scheduled_individually: !!form.scheduled_individually,
      contact_name:           form.contact_name.trim() || null,
      contact_title:          form.contact_title.trim() || null,
      contact_phone:          form.contact_phone.trim() || null,
      contact_email:          form.contact_email.trim() || null,
    };
    setSavingExam(true);
    setFormError('');
    const isEdit = !!modalExam?.id;
    const { data, error } = isEdit
      ? await supabase.from('union_exams').update(payload).eq('id', modalExam.id).select().single()
      : await supabase.from('union_exams').insert({ ...payload, union_id: userId }).select().single();
    if (error || !data) { setSavingExam(false); setFormError(t('exams.union.saveFailed')); return; }

    // Replace the whole set: simpler than diffing, and the list is a handful of rows.
    const delRes = isEdit ? await supabase.from('union_exam_sessions').delete().eq('exam_id', data.id) : { error: null };
    const insRes = rows.length
      ? await supabase.from('union_exam_sessions').insert(rows.map(r => ({ ...r, exam_id: data.id }))).select()
      : { data: [], error: null };
    setSavingExam(false);
    if (delRes.error || insRes.error) { setFormError(t('exams.union.sessionsSaveFailed')); return; }

    const saved = { ...data, sessions: insRes.data || [] };
    setExams(prev => isEdit ? prev.map(e => e.id === saved.id ? saved : e) : [...prev, saved]);
    setModalExam(null);
  };

  const handleDeleteExam = async (exam) => {
    const linkedCount = links.filter(l => l.exam_id === exam.id).length;
    const message = linkedCount > 0
      ? t('exams.union.deleteConfirmLinked', { name: exam.name, count: linkedCount })
      : t('exams.union.deleteConfirm', { name: exam.name });
    if (!window.confirm(message)) return;
    setActionError('');
    setBusyExamId(exam.id);
    const { error } = await supabase.from('union_exams').delete().eq('id', exam.id);
    setBusyExamId(null);
    if (error) { setActionError(t('exams.union.deleteFailed')); return; }
    // Join rows cascade in the database; mirror that locally.
    setExams(prev => prev.filter(e => e.id !== exam.id));
    setLinks(prev => prev.filter(l => l.exam_id !== exam.id));
  };

  // ── Linking ──
  const handleLink = async (exam, appr) => {
    setActionError('');
    setBusyExamId(exam.id);
    const { error } = await supabase.from('apprenticeship_exam_requirements').insert({ apprenticeship_id: appr.id, exam_id: exam.id });
    setBusyExamId(null);
    // 23505 = already linked (e.g. from another tab) -- the desired end state, so treat as success.
    if (error && error.code !== '23505') { setActionError(t('exams.union.linkFailed')); return; }
    setLinks(prev => prev.some(l => l.exam_id === exam.id && l.apprenticeship_id === appr.id) ? prev : [...prev, { exam_id: exam.id, apprenticeship_id: appr.id }]);
  };

  const handleUnlink = async (exam, appr) => {
    setActionError('');
    setBusyExamId(exam.id);
    const { error } = await supabase.from('apprenticeship_exam_requirements').delete().eq('exam_id', exam.id).eq('apprenticeship_id', appr.id);
    setBusyExamId(null);
    if (error) { setActionError(t('exams.union.linkFailed')); return; }
    setLinks(prev => prev.filter(l => !(l.exam_id === exam.id && l.apprenticeship_id === appr.id)));
  };

  // ── Requirements ──
  const handleSaveRequirements = async (appr, next) => {
    const { error } = await supabase.from('apprenticeships').update({ requirements: next }).eq('id', appr.id).select('id').single();
    if (error) return false;
    onApprenticeshipUpdated(appr.id, { requirements: next });
    return true;
  };

  const reqApprs = apprenticeships.filter(a => showArchivedReqs || !a.archived_at);
  const archivedCount = apprenticeships.filter(a => a.archived_at).length;

  return (
    <div>
      {/* ── Official header bar ── */}
      <header style={{
        backgroundColor: NAVY_DEEP, border: '1px solid rgba(255,255,255,0.07)', borderBottom: `3px solid ${AMBER}`,
        borderRadius: '10px 10px 0 0', padding: '16px 20px 14px', marginBottom: '16px',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap',
      }}>
        <div style={{ minWidth: 0 }}>
          <p style={{ ...kicker, color: AMBER, marginBottom: '4px' }}>
            {[unionAccount?.union_name, unionAccount?.local_number && t('exams.union.localPrefix', { number: unionAccount.local_number })].filter(Boolean).join(' · ')}
          </p>
          <h2 style={{ fontFamily: FONT_HEAD, fontSize: '2rem', letterSpacing: '0.04em', color: WHITE, lineHeight: 1 }}>
            {t('exams.union.title')}
          </h2>
          <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', marginTop: '4px', maxWidth: '520px', lineHeight: 1.5 }}>
            {t('exams.union.subtitle')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '18px' }}>
          {[
            { value: exams.length, label: t('exams.union.statExams', { count: exams.length }) },
            { value: postingsWithExams, label: t('exams.union.statPostingsWithExams', { count: postingsWithExams }) },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: FONT_HEAD, fontSize: '1.7rem', color: WHITE, lineHeight: 1 }}>{loaded ? s.value : '–'}</div>
              <div style={{ ...kicker, fontSize: '0.58rem', color: 'rgba(255,255,255,0.4)' }}>{s.label}</div>
            </div>
          ))}
        </div>
      </header>

      {/* ── View switch + primary action ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div role="tablist" style={{ display: 'flex', gap: '4px', background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '4px' }}>
          {[['exams', t('exams.union.viewExams')], ['requirements', t('exams.union.viewRequirements')]].map(([key, label]) => (
            <button
              key={key}
              role="tab"
              aria-selected={view === key}
              onClick={() => setView(key)}
              style={{
                background: view === key ? AMBER : 'transparent', color: view === key ? NAVY : 'rgba(255,255,255,0.6)',
                border: 'none', borderRadius: '6px', padding: '7px 14px',
                fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', fontFamily: FONT_BODY,
              }}
            >
              {label}
            </button>
          ))}
        </div>
        {view === 'exams' && (
          <button onClick={openCreate} style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.85rem', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY }}>
            {t('exams.union.newExamButton')}
          </button>
        )}
      </div>

      {actionError && (
        <p role="alert" style={{ backgroundColor: 'rgba(220,38,38,0.1)', border: '1px solid rgba(220,38,38,0.35)', color: '#F87171', borderRadius: '8px', padding: '9px 12px', fontSize: '0.82rem', fontWeight: 600, marginBottom: '12px' }}>
          {actionError}
        </p>
      )}

      {/* ── EXAMS ── */}
      {view === 'exams' && (
        !loaded ? (
          <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.88rem' }}>{t('common.loading')}</p>
        ) : loadError ? (
          <div role="alert" data-exams-load-failed style={{ background: NAVY_MID, borderRadius: '8px', padding: '24px', color: '#F87171', fontSize: '0.88rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <span>{t('exams.union.loadFailed')}</span>
            <button type="button" onClick={() => setReloadKey(k => k + 1)} style={{ ...ghostBtn, minHeight: '44px', fontSize: '14px', padding: '10px 16px' }}>
              {t('common.retry')}
            </button>
          </div>
        ) : sortedExams.length === 0 ? (
          <div style={{ background: NAVY_MID, border: '1px dashed rgba(245,166,35,0.35)', borderRadius: '10px', padding: '28px 22px', maxWidth: '620px' }}>
            <p style={{ ...kicker, color: AMBER, marginBottom: '6px' }}>{t('exams.union.examNotice')}</p>
            <h3 style={{ fontFamily: FONT_HEAD, fontSize: '1.5rem', color: WHITE, letterSpacing: '0.03em', marginBottom: '6px' }}>{t('exams.union.emptyTitle')}</h3>
            <p style={{ fontSize: '0.86rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, marginBottom: '16px' }}>{t('exams.union.emptyBody')}</p>
            <button onClick={openCreate} style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.85rem', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY }}>
              {t('exams.union.newExamButton')}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {sortedExams.map(exam => {
              const linkedIds = new Set(links.filter(l => l.exam_id === exam.id).map(l => l.apprenticeship_id));
              return (
                <ExamBulletin
                  key={exam.id}
                  exam={exam}
                  linkedApprs={[...linkedIds].map(id => apprById[id]).filter(Boolean)}
                  linkableApprs={linkTargets.filter(a => !linkedIds.has(a.id))}
                  hasAnyApprenticeships={apprenticeships.length > 0}
                  busy={busyExamId === exam.id}
                  onEdit={() => openEdit(exam)}
                  onDelete={() => handleDeleteExam(exam)}
                  onLink={appr => handleLink(exam, appr)}
                  onUnlink={appr => handleUnlink(exam, appr)}
                />
              );
            })}
          </div>
        )
      )}

      {/* ── PROGRAM REQUIREMENTS ── */}
      {view === 'requirements' && (
        <div style={{ maxWidth: '860px' }}>
          <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.55, marginBottom: '14px', maxWidth: '640px' }}>
            {t('exams.union.requirementsIntro')}
          </p>
          {apprenticeships.length === 0 ? (
            <div style={{ background: NAVY_MID, borderRadius: '8px', padding: '28px', textAlign: 'center' }}>
              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.88rem', marginBottom: '14px' }}>{t('exams.union.noPostingsForRequirements')}</p>
              <button onClick={onPostApprenticeship} style={{ background: AMBER, border: 'none', color: NAVY, fontWeight: 700, fontSize: '0.85rem', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY }}>
                {t('union.dashboard.apprenticeships.postNewButton')}
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {reqApprs.map(appr => (
                  <RequirementsCard
                    key={appr.id}
                    appr={appr}
                    linkedExams={links.filter(l => l.apprenticeship_id === appr.id).map(l => examById[l.exam_id]).filter(Boolean)}
                    onSave={handleSaveRequirements}
                    onGoToExams={() => setView('exams')}
                  />
                ))}
              </div>
              {archivedCount > 0 && (
                <button onClick={() => setShowArchivedReqs(v => !v)} style={{ ...ghostBtn, marginTop: '14px' }}>
                  {showArchivedReqs ? t('exams.union.hideArchived') : t('exams.union.showArchived', { count: archivedCount })}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* ── Create / edit exam modal ── */}
      {modalExam && (
        <div
          onClick={e => { if (e.target === e.currentTarget) closeModal(); }}
          style={{ position: 'fixed', inset: 0, zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
        >
          <div role="dialog" aria-modal="true" aria-labelledby="exam-modal-title" style={{
            backgroundColor: NAVY, borderRadius: '14px', border: '1px solid rgba(255,255,255,0.12)', borderTop: `3px solid ${AMBER}`,
            padding: '24px 22px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto',
          }}>
            <p style={{ ...kicker, color: AMBER, marginBottom: '2px' }}>{t('exams.union.examNotice')}</p>
            <h2 id="exam-modal-title" style={{ fontFamily: FONT_HEAD, fontSize: '1.9rem', color: WHITE, letterSpacing: '0.04em', marginBottom: '16px' }}>
              {modalExam.id ? t('exams.union.editExamHeading') : t('exams.union.newExamHeading')}
            </h2>

            <div style={fld}>
              <label htmlFor="exam-name" style={lbl}>{t('exams.union.nameLabel')} *</label>
              <input id="exam-name" style={inp} maxLength={120} placeholder={t('exams.union.namePlaceholder')} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>

            <div style={fld}>
              <label htmlFor="exam-description" style={lbl}>{t('union.dashboard.apprenticeships.descriptionLabel')}</label>
              <textarea id="exam-description" style={txt} placeholder={t('exams.union.descriptionPlaceholder')} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>

            {/* Individually-scheduled exams have no dates to give, so the whole session list
                goes away rather than sitting there empty and inviting a stray entry. */}
            <div style={{ ...fld, backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                <input
                  data-individually-scheduled
                  type="checkbox"
                  checked={!!form.scheduled_individually}
                  onChange={e => setForm(f => ({ ...f, scheduled_individually: e.target.checked }))}
                  style={{ width: '17px', height: '17px', marginTop: '2px', accentColor: AMBER, flexShrink: 0, cursor: 'pointer' }}
                />
                <span>
                  <span style={{ display: 'block', fontSize: '0.86rem', fontWeight: 700, color: WHITE }}>{t('exams.union.scheduledIndividuallyLabel')}</span>
                  <span style={{ display: 'block', fontSize: '0.76rem', color: 'rgba(255,255,255,0.5)', marginTop: '2px', lineHeight: 1.45 }}>{t('exams.union.scheduledIndividuallyHint')}</span>
                </span>
              </label>
            </div>

            {!form.scheduled_individually && (
              <div style={fld}>
                <label style={lbl}>{t('exams.union.sessionsLabel')}</label>
                <p style={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.45)', marginTop: '-2px', marginBottom: '8px', lineHeight: 1.45 }}>
                  {t('exams.union.sessionsHint')}
                </p>
                {sessions.length === 0 && (
                  <p data-no-sessions style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginBottom: '8px' }}>{t('exams.union.noSessionsYet')}</p>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '10px' }}>
                  {sessions.map((s, i) => (
                    <div key={i} data-exam-session-row style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <label htmlFor={`exam-session-date-${i}`} style={{ ...lbl, fontSize: '0.7rem' }}>{t('exams.union.sessionTestDateLabel')}</label>
                        <input
                          id={`exam-session-date-${i}`} data-session-date style={inp} type="date"
                          value={s.test_date} onChange={e => updateSession(i, 'test_date', e.target.value)}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <label htmlFor={`exam-session-deadline-${i}`} style={{ ...lbl, fontSize: '0.7rem' }}>{t('exams.union.sessionDeadlineLabel')}</label>
                        <input
                          id={`exam-session-deadline-${i}`} data-session-deadline style={inp} type="date"
                          max={s.test_date || undefined}
                          value={s.registration_deadline} onChange={e => updateSession(i, 'registration_deadline', e.target.value)}
                        />
                      </div>
                      <button
                        type="button" data-remove-session onClick={() => removeSession(i)}
                        aria-label={t('exams.union.removeSessionLabel')}
                        style={{ ...dangerBtn, height: '38px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <IconX />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button" data-add-session onClick={addSession}
                  style={{ background: 'transparent', border: `1.5px dashed ${AMBER}`, color: AMBER, fontWeight: 700, fontSize: '0.8rem', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY }}
                >
                  {t('exams.union.addSessionButton')}
                </button>
              </div>
            )}

            <div style={fld}>
              <label htmlFor="exam-location" style={lbl}>{t('exams.shared.locationLabel')}</label>
              <input id="exam-location" style={inp} placeholder={t('exams.union.locationPlaceholder')} value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} />
            </div>

            <div className="ud-two-col" style={g2}>
              <div>
                <label htmlFor="exam-score" style={lbl}>{t('exams.shared.passingScoreLabel')}</label>
                <input id="exam-score" style={inp} placeholder={t('exams.union.passingScorePlaceholder')} value={form.passing_score} onChange={e => setForm(f => ({ ...f, passing_score: e.target.value }))} />
              </div>
              <div>
                <label htmlFor="exam-fee" style={lbl}>{t('exams.shared.examFeeLabel')}</label>
                <input id="exam-fee" style={inp} placeholder={t('exams.union.examFeePlaceholder')} value={form.exam_fee} onChange={e => setForm(f => ({ ...f, exam_fee: e.target.value }))} />
              </div>
            </div>

            <div style={fld}>
              <label htmlFor="exam-guide" style={lbl}>{t('exams.union.studyGuideUrlLabel')}</label>
              <input id="exam-guide" style={inp} type="url" inputMode="url" placeholder="https://" value={form.study_guide_url} onChange={e => setForm(f => ({ ...f, study_guide_url: e.target.value }))} />
            </div>

            {/* Named contact -- entirely optional; workers see nothing extra unless it's filled in. */}
            <div style={{ ...fld, borderTop: '1px dashed rgba(255,255,255,0.12)', paddingTop: '14px' }}>
              <p style={{ fontSize: '0.86rem', fontWeight: 700, color: WHITE, marginBottom: '2px' }}>{t('exams.union.contactHeading')}</p>
              <p style={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.45)', marginBottom: '10px' }}>{t('exams.union.contactHint')}</p>
              <div className="ud-two-col" style={g2}>
                <div>
                  <label htmlFor="exam-contact-name" style={lbl}>{t('exams.union.contactNameLabel')}</label>
                  <input id="exam-contact-name" data-contact-name style={inp} maxLength={120} placeholder={t('exams.union.contactNamePlaceholder')} value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} />
                </div>
                <div>
                  <label htmlFor="exam-contact-title" style={lbl}>{t('exams.union.contactTitleLabel')}</label>
                  <input id="exam-contact-title" data-contact-title style={inp} maxLength={120} placeholder={t('exams.union.contactTitlePlaceholder')} value={form.contact_title} onChange={e => setForm(f => ({ ...f, contact_title: e.target.value }))} />
                </div>
              </div>
              <div className="ud-two-col" style={{ ...g2, marginBottom: 0 }}>
                <div>
                  <label htmlFor="exam-contact-phone" style={lbl}>{t('exams.union.contactPhoneLabel')}</label>
                  <input id="exam-contact-phone" data-contact-phone style={inp} type="tel" inputMode="tel" maxLength={40} placeholder="(312) 555-0100" value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} />
                </div>
                <div>
                  <label htmlFor="exam-contact-email" style={lbl}>{t('exams.union.contactEmailLabel')}</label>
                  <input id="exam-contact-email" data-contact-email style={inp} type="email" inputMode="email" maxLength={160} placeholder="you@local134.org" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} />
                </div>
              </div>
            </div>

            {formError && <p role="alert" style={{ fontSize: '0.82rem', fontWeight: 600, color: '#F87171', marginBottom: '10px' }}>{formError}</p>}

            <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
              <button
                onClick={handleSaveExam}
                disabled={savingExam}
                style={{ flex: 1, padding: '11px', borderRadius: '8px', border: 'none', backgroundColor: AMBER, color: NAVY, fontFamily: FONT_BODY, fontSize: '14px', fontWeight: 700, cursor: 'pointer', opacity: savingExam ? 0.7 : 1 }}
              >
                {savingExam ? t('union.dashboard.shared.savingButton') : modalExam.id ? t('union.dashboard.apprModal.saveChangesButton') : t('exams.union.createExamButton')}
              </button>
              <button
                onClick={closeModal}
                style={{ padding: '11px 18px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', backgroundColor: 'transparent', color: 'rgba(255,255,255,0.7)', fontFamily: FONT_BODY, fontSize: '14px', cursor: 'pointer' }}
              >
                {t('union.dashboard.shared.cancelButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
